import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Link2Off, Lock, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import { useGradingTemplatesQuery } from '~/pages/SchoolSettings/useGradingTemplatesQuery';
import {
  useApplyGradingTemplate,
  useCreateCourseGradebook,
  useGradingTemplatePreview,
  useSaveCustomAsGradingTemplate,
  useUnlinkGradingTemplate,
  useUpdateCourseGradebook,
} from './useCourseGradebooks';

interface TermOption {
  id: string;
  name: string;
  position: number | null;
}

interface OfferingOption {
  id: string;
  section: string;
  term: { id: string; name: string; position: number | null };
}

interface TemplateCategory {
  id: string;
  name: string;
  weight: number;
  dropLowest: number;
  position: number;
  locked?: boolean;
  lockedByGradebookNames?: ReadonlyArray<string>;
}

interface GradingTemplateOption {
  id: string;
  name: string;
  description?: string | null;
  categories: ReadonlyArray<TemplateCategory>;
}

interface TranscriptCourseOption {
  id: string;
  name: string;
  creditValue: number | null;
  termCount: number;
}

interface EditingLine {
  id: string;
  name: string;
  creditValue: number | null;
  termCount: number;
  classOfferingIds: ReadonlyArray<string>;
  categories: ReadonlyArray<TemplateCategory>;
  gradingTemplate?: GradingTemplateOption | null;
  gradingTemplateOutOfDate?: boolean;
}

interface CategoryRow {
  id?: string;
  name: string;
  weight: string;
  dropLowest: string;
  locked?: boolean;
  lockedByGradebookNames?: ReadonlyArray<string>;
}

interface CategoryDiff {
  name: string;
  reason?: string | null;
  current?: { name: string; weight: number; dropLowest: number; position: number } | null;
  proposed?: { name: string; weight: number; dropLowest: number; position: number } | null;
}

interface TemplatePreview {
  digest: string;
  changed: boolean;
  outOfDate: boolean;
  applicable: boolean;
  conflicts: ReadonlyArray<string>;
  additions: ReadonlyArray<CategoryDiff>;
  updates: ReadonlyArray<CategoryDiff>;
  removals: ReadonlyArray<CategoryDiff>;
  retained: ReadonlyArray<CategoryDiff>;
  unchanged: ReadonlyArray<CategoryDiff>;
}

interface ReviewedTemplatePreview extends TemplatePreview {
  templateId: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schoolId: string;
  courseId: string;
  terms: ReadonlyArray<TermOption>;
  classOfferings: ReadonlyArray<OfferingOption>;
  transcriptCourses: ReadonlyArray<TranscriptCourseOption>;
  editing?: EditingLine | null;
  onCreated: () => void | Promise<unknown>;
  onRefreshError?: (message: string) => void;
}

const emptyCategory = (): CategoryRow => ({ name: '', weight: '', dropLowest: '0' });

const userErrorMessage = (errors?: ReadonlyArray<{ message: string }> | null) =>
  errors?.map((item) => item.message).join(', ') || null;

export default function CourseGradebookDialog({
  open,
  onOpenChange,
  schoolId,
  courseId,
  terms,
  classOfferings,
  transcriptCourses,
  editing = null,
  onCreated,
  onRefreshError,
}: Props) {
  const [createCourseGradebook, { loading: creating }] = useCreateCourseGradebook();
  const [updateCourseGradebook, { loading: updating }] = useUpdateCourseGradebook();
  const [loadPreview, { loading: previewing }] = useGradingTemplatePreview();
  const [applyTemplate, { loading: applying }] = useApplyGradingTemplate();
  const [unlinkTemplate, { loading: unlinking }] = useUnlinkGradingTemplate();
  const [saveCustomTemplate, { loading: savingTemplate }] = useSaveCustomAsGradingTemplate();
  const { data: templateData, loading: loadingTemplates, refetch: refetchTemplates } = useGradingTemplatesQuery(
    open ? schoolId : undefined
  );
  const templates = (templateData?.school?.gradingTemplates ?? []) as ReadonlyArray<GradingTemplateOption>;
  const [refreshing, setRefreshing] = useState(false);
  const loading = creating || updating || previewing || applying || unlinking || savingTemplate || refreshing;

  const [mode, setMode] = useState<'template' | 'custom'>(
    editing?.gradingTemplate ? 'template' : 'custom'
  );
  const [linkedTemplateId, setLinkedTemplateId] = useState(editing?.gradingTemplate?.id ?? null);
  const [selectedTemplateId, setSelectedTemplateId] = useState(editing?.gradingTemplate?.id ?? '');
  const [preview, setPreview] = useState<ReviewedTemplatePreview | null>(null);
  const previewRequestId = useRef(0);
  const selectedTemplateIdRef = useRef(editing?.gradingTemplate?.id ?? '');
  const [name, setName] = useState(editing?.name ?? '');
  const [transcriptCourseId, setTranscriptCourseId] = useState('');
  const [creditValue, setCreditValue] = useState(
    editing?.creditValue == null ? '' : String(editing.creditValue)
  );
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  const [termIds, setTermIds] = useState<Set<string>>(() => {
    if (!editing) return new Set(terms.map((term) => term.id));
    const covered = classOfferings
      .filter((offering) => editing.classOfferingIds.includes(offering.id))
      .map((offering) => offering.term.id);
    return new Set(covered.length > 0 ? covered : terms.map((term) => term.id));
  });
  const [offeringIds, setOfferingIds] = useState<Set<string>>(
    () => new Set(editing ? editing.classOfferingIds : classOfferings.map((offering) => offering.id))
  );
  const [categories, setCategories] = useState<CategoryRow[]>(() =>
    editing && editing.categories.length > 0
      ? [...editing.categories]
          .sort((a, b) => a.position - b.position)
          .map((category) => ({
            id: category.id,
            name: category.name,
            weight: String(category.weight),
            dropLowest: String(category.dropLowest),
            locked: category.locked,
            lockedByGradebookNames: category.lockedByGradebookNames,
          }))
      : [emptyCategory()]
  );
  const [error, setError] = useState<string | null>(null);
  const [refreshWarning, setRefreshWarning] = useState<string | null>(null);
  const [pendingPresetSeriesId, setPendingPresetSeriesId] = useState<string | null>(null);
  const [terminalCommitted, setTerminalCommitted] = useState(false);

  // The dialog remains mounted between opens. Reinitialize every session so a
  // cancelled create never leaks its name, mode, preset, or categories into
  // the next gradebook.
  useEffect(() => {
    if (!open) return;

    setMode(editing?.gradingTemplate ? 'template' : 'custom');
    setLinkedTemplateId(editing?.gradingTemplate?.id ?? null);
    const initialTemplateId = editing?.gradingTemplate?.id ?? '';
    selectedTemplateIdRef.current = initialTemplateId;
    previewRequestId.current += 1;
    setSelectedTemplateId(initialTemplateId);
    setPreview(null);
    setName(editing?.name ?? '');
    setTranscriptCourseId('');
    setCreditValue(editing?.creditValue == null ? '' : String(editing.creditValue));
    setSaveAsTemplate(false);
    setNewTemplateName('');
    setNewTemplateDescription('');
    const coveredTerms = editing
      ? classOfferings
          .filter((offering) => editing.classOfferingIds.includes(offering.id))
          .map((offering) => offering.term.id)
      : [];
    setTermIds(new Set(coveredTerms.length > 0 ? coveredTerms : terms.map((term) => term.id)));
    setOfferingIds(new Set(editing ? editing.classOfferingIds : classOfferings.map((offering) => offering.id)));
    setCategories(
      editing && editing.categories.length > 0
        ? [...editing.categories]
            .sort((a, b) => a.position - b.position)
            .map((category) => ({
              id: category.id,
              name: category.name,
              weight: String(category.weight),
              dropLowest: String(category.dropLowest),
              locked: category.locked,
              lockedByGradebookNames: category.lockedByGradebookNames,
            }))
        : [emptyCategory()]
    );
    setError(null);
    setRefreshWarning(null);
    setPendingPresetSeriesId(null);
    setTerminalCommitted(false);
    setRefreshing(false);
  }, [open, editing, terms, classOfferings]);

  const orderedTerms = useMemo(
    () => [...terms].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    [terms]
  );
  const visibleOfferings = useMemo(
    () =>
      classOfferings
        .filter((offering) => termIds.has(offering.term.id))
        .sort(
          (a, b) =>
            (a.term.position ?? 0) - (b.term.position ?? 0) || a.section.localeCompare(b.section)
        ),
    [classOfferings, termIds]
  );
  const selectedOfferingIds = useMemo(
    () => visibleOfferings.filter((offering) => offeringIds.has(offering.id)).map((offering) => offering.id),
    [visibleOfferings, offeringIds]
  );
  const selectedTermCount = useMemo(
    () => new Set(visibleOfferings.filter((offering) => offeringIds.has(offering.id)).map((offering) => offering.term.id)).size,
    [visibleOfferings, offeringIds]
  );
  const selectedTemplate = templates.find((template) => template.id === selectedTemplateId) ??
    (editing?.gradingTemplate?.id === selectedTemplateId ? editing.gradingTemplate : undefined);
  const perTermCredit = useMemo(() => {
    const credit = Number(creditValue);
    if (creditValue.trim() === '' || !Number.isFinite(credit) || credit <= 0) return null;
    if (selectedTermCount <= 1) return 'Applies to 1 term.';

    return `${credit} across ${selectedTermCount} terms = ${Number((credit / selectedTermCount).toFixed(4))} per term.`;
  }, [creditValue, selectedTermCount]);

  const toggleSetValue = (
    setter: React.Dispatch<React.SetStateAction<Set<string>>>,
    id: string,
    checked: boolean
  ) => {
    setter((current) => {
      const next = new Set(current);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const updateCategory = (index: number, patch: Partial<CategoryRow>) => {
    setCategories((current) => current.map((row, rowIndex) =>
      rowIndex === index ? { ...row, ...patch } : row
    ));
  };

  const selectTranscriptCourse = (id: string) => {
    setTranscriptCourseId(id);
    const selected = transcriptCourses.find((course) => course.id === id);
    if (selected) {
      setName((current) => current || selected.name);
      setCreditValue(selected.creditValue == null ? '' : String(selected.creditValue));
    }
  };

  const chooseMode = (nextMode: string) => {
    setError(null);
    previewRequestId.current += 1;
    setPreview(null);
    if (nextMode === 'custom' && linkedTemplateId) {
      setError('Unlink the preset before editing these course-owned categories as custom.');
      return;
    }
    if (nextMode === 'template') setTranscriptCourseId('');
    setMode(nextMode as 'template' | 'custom');
  };

  const chooseTemplate = (templateId: string) => {
    selectedTemplateIdRef.current = templateId;
    previewRequestId.current += 1;
    setSelectedTemplateId(templateId);
    setPreview(null);
    setError(null);
  };

  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && loading) return;

    onOpenChange(nextOpen);
  };

  const refreshCommittedData = async ({
    savedLabel,
    includeTemplates = false,
    terminal = true,
    closeOnSuccess = true,
  }: {
    savedLabel: string;
    includeTemplates?: boolean;
    terminal?: boolean;
    closeOnSuccess?: boolean;
  }) => {
    setRefreshing(true);
    try {
      const refreshes: Array<Promise<unknown>> = [Promise.resolve().then(() => onCreated())];
      if (includeTemplates) refreshes.push(Promise.resolve().then(() => refetchTemplates()));
      const results = await Promise.allSettled(refreshes);
      if (results.some((result) => result.status === 'rejected')) {
        const message = `${savedLabel} was saved, but current data could not be refreshed. Reload the course before making more changes.`;
        setRefreshWarning(message);
        onRefreshError?.(message);
        if (terminal) setTerminalCommitted(true);
        return false;
      }

      setRefreshWarning(null);
      if (closeOnSuccess) onOpenChange(false);
      return true;
    } finally {
      setRefreshing(false);
    }
  };

  const handlePreview = async () => {
    if (!editing || !selectedTemplateId) return;
    const requestedTemplateId = selectedTemplateId;
    const requestId = ++previewRequestId.current;
    setError(null);
    setPreview(null);
    try {
      const result = await loadPreview({
        variables: { gradebookSeriesId: editing.id, templateId: requestedTemplateId },
      });
      if (
        previewRequestId.current !== requestId ||
        selectedTemplateIdRef.current !== requestedTemplateId
      ) return;
      const nextPreview = result.data?.gradingTemplatePreview;
      if (nextPreview) {
        setPreview({ ...(nextPreview as TemplatePreview), templateId: requestedTemplateId });
      }
    } catch (caught) {
      if (
        previewRequestId.current !== requestId ||
        selectedTemplateIdRef.current !== requestedTemplateId
      ) return;
      setError(caught instanceof Error ? caught.message : 'Unable to preview this preset.');
    }
  };

  const handleApply = async () => {
    if (
      !editing ||
      !selectedTemplateId ||
      preview?.templateId !== selectedTemplateId ||
      !preview.applicable ||
      terminalCommitted
    ) return;
    setError(null);
    try {
      const result = await applyTemplate({
        variables: {
          input: {
            gradebookSeriesId: editing.id,
            templateId: selectedTemplateId,
            previewDigest: preview.digest,
          },
        },
      });
      const message = userErrorMessage(result.data?.applyGradingTemplate?.userErrors);
      if (message) {
        setPreview(null);
        setError(message);
        return;
      }
      await refreshCommittedData({ savedLabel: 'The grading preset' });
    } catch (caught) {
      setPreview(null);
      setError(caught instanceof Error ? caught.message : 'Unable to apply this grading preset.');
    }
  };

  const handleUnlink = async () => {
    if (!editing) return;
    setError(null);
    try {
      const result = await unlinkTemplate({
        variables: { input: { gradebookSeriesId: editing.id } },
      });
      const message = userErrorMessage(result.data?.unlinkGradingTemplate?.userErrors);
      if (message) {
        setError(message);
        return;
      }
      setLinkedTemplateId(null);
      selectedTemplateIdRef.current = '';
      previewRequestId.current += 1;
      setSelectedTemplateId('');
      setPreview(null);
      setMode('custom');
      await refreshCommittedData({
        savedLabel: 'The preset link',
        closeOnSuccess: false,
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to unlink this grading preset.');
    }
  };

  const saveCustomPreset = async (seriesId: string) => {
    if (!newTemplateName.trim()) {
      setError('A name is required to save this custom setup as a preset.');
      return false;
    }
    try {
      const result = await saveCustomTemplate({
        variables: {
          input: {
            gradebookSeriesId: seriesId,
            name: newTemplateName.trim(),
            description: newTemplateDescription.trim() || null,
          },
        },
      });
      const message = userErrorMessage(result.data?.saveCustomAsGradingTemplate?.userErrors);
      if (message) {
        setError(`The gradebook was saved, but the preset was not: ${message}`);
        setPendingPresetSeriesId(seriesId);
        return false;
      }
      setPendingPresetSeriesId(null);
      return true;
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : 'Unable to save the preset.';
      setError(`The gradebook was saved, but the preset was not confirmed: ${message}`);
      setPendingPresetSeriesId(seriesId);
      return false;
    }
  };

  const handleSubmit = async () => {
    setError(null);
    if (terminalCommitted) {
      onOpenChange(false);
      return;
    }
    if (pendingPresetSeriesId) {
      if (await saveCustomPreset(pendingPresetSeriesId)) {
        await refreshCommittedData({
          savedLabel: 'The gradebook and grading preset',
          includeTemplates: true,
        });
      }
      return;
    }
    if (!name.trim()) {
      setError('Gradebook name is required.');
      return;
    }
    if (selectedOfferingIds.length === 0) {
      setError('Select at least one class.');
      return;
    }
    if (mode === 'template' && !selectedTemplateId) {
      setError('Select a grading preset.');
      return;
    }
    if (mode === 'custom' && saveAsTemplate && !newTemplateName.trim()) {
      setError('A name is required to save this custom setup as a preset.');
      return;
    }

    const credit = creditValue.trim() === '' ? null : Number(creditValue);
    if (credit != null && (!Number.isFinite(credit) || credit < 0)) {
      setError('Transcripted credit must be zero or greater.');
      return;
    }
    const payloadCategories = categories
      .filter((row) => row.name.trim() !== '')
      .map((row, index) => ({
        ...(row.id ? { id: row.id } : {}),
        name: row.name.trim(),
        weight: Number(row.weight) || 0,
        dropLowest: Number.parseInt(row.dropLowest, 10) || 0,
        position: index,
      }));

    let seriesId = editing?.id;
    try {
      if (editing) {
        const result = await updateCourseGradebook({
          variables: {
            input: {
              id: editing.id,
              transcriptName: name.trim(),
              creditValue: credit,
              classOfferingIds: selectedOfferingIds,
              ...(mode === 'custom' ? { categories: payloadCategories } : {}),
            },
          },
        });
        const message = userErrorMessage(result.data?.updateCourseGradebook?.userErrors);
        if (message) {
          setError(message);
          return;
        }
      } else {
        const result = await createCourseGradebook({
          variables: {
            input: {
              courseId,
              name: name.trim(),
              ...(transcriptCourseId ? { transcriptCourseId } : {}),
              transcriptName: name.trim(),
              creditValue: credit,
              classOfferingIds: selectedOfferingIds,
              ...(mode === 'template'
                ? { templateId: selectedTemplateId }
                : { categories: payloadCategories }),
            },
          },
        });
        const message = userErrorMessage(result.data?.createCourseGradebook?.userErrors);
        if (message) {
          setError(message);
          return;
        }
        seriesId = result.data?.createCourseGradebook?.gradebooks?.[0]?.transcriptCourse?.id;
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to save the gradebook.');
      return;
    }

    if (!seriesId) {
      setTerminalCommitted(true);
      const message = 'The gradebook was saved, but its Transcript course id was missing. Reload before making more changes.';
      setRefreshWarning(message);
      onRefreshError?.(message);
      await Promise.resolve().then(() => onCreated()).catch(() => undefined);
      return;
    }

    if (mode === 'custom' && saveAsTemplate) {
      if (!(await saveCustomPreset(seriesId))) {
        await refreshCommittedData({
          savedLabel: 'The gradebook',
          terminal: false,
          closeOnSuccess: false,
        });
        return;
      }
      await refreshCommittedData({
        savedLabel: 'The gradebook and grading preset',
        includeTemplates: true,
      });
      return;
    }

    await refreshCommittedData({ savedLabel: 'The gradebook' });
  };

  const weightTotal = categories.reduce((sum, row) => sum + (parseFloat(row.weight) || 0), 0);

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${editing.name}` : 'Add gradebook'}</DialogTitle>
          <DialogDescription>
            Define one Transcript course across the selected classes. Start from a reusable
            school preset or maintain a custom category setup owned by this course.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="course-gradebook-name">Name</Label>
              <Input id="course-gradebook-name" value={name} onChange={(event) => setName(event.target.value)} placeholder="Literature" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="course-gradebook-credit">Transcripted credit for course</Label>
              <Input id="course-gradebook-credit" type="number" min="0" step="0.001" value={creditValue} onChange={(event) => setCreditValue(event.target.value)} placeholder="Optional" />
              {perTermCredit && <p className="text-xs text-muted-foreground">{perTermCredit}</p>}
            </div>
          </div>

          {!editing && mode === 'custom' && transcriptCourses.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="course-gradebook-transcript">Transcript line</Label>
              <select id="course-gradebook-transcript" className="w-full rounded-md border bg-background px-3 py-2 text-sm" value={transcriptCourseId} onChange={(event) => selectTranscriptCourse(event.target.value)}>
                <option value="">Create a new one from the name above</option>
                {transcriptCourses.map((course) => (
                  <option key={course.id} value={course.id}>{course.name}{course.creditValue == null ? '' : ` · ${course.creditValue} credits`}</option>
                ))}
              </select>
            </div>
          )}

          <div className="space-y-2">
            <Label>Terms</Label>
            <div className="flex flex-wrap gap-4">
              {orderedTerms.map((term) => (
                <label key={term.id} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={termIds.has(term.id)} aria-label={`Term ${term.name}`} onCheckedChange={(checked) => toggleSetValue(setTermIds, term.id, checked === true)} />
                  {term.name}
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Classes</Label>
            {visibleOfferings.length === 0 ? <p className="text-sm text-muted-foreground">No classes in the selected terms.</p> : (
              <div className="flex flex-wrap gap-4">
                {visibleOfferings.map((offering) => {
                  const label = `${offering.term.name} · Section ${offering.section}`;
                  return (
                    <label key={offering.id} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={offeringIds.has(offering.id)} aria-label={label} onCheckedChange={(checked) => toggleSetValue(setOfferingIds, offering.id, checked === true)} />
                      {label}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div className="space-y-3 rounded-md border p-4">
            <Label>Category setup</Label>
            <RadioGroup value={mode} onValueChange={chooseMode} className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 rounded-md border p-3 text-sm">
                <RadioGroupItem value="template" aria-label="From Template" />
                From Template
              </label>
              <label className="flex items-center gap-2 rounded-md border p-3 text-sm">
                <RadioGroupItem value="custom" aria-label="Custom" />
                Custom
              </label>
            </RadioGroup>

            {mode === 'template' ? (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="course-gradebook-template">Grading preset</Label>
                  <select
                    id="course-gradebook-template"
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    value={selectedTemplateId}
                    onChange={(event) => chooseTemplate(event.target.value)}
                    disabled={loadingTemplates}
                  >
                    <option value="">{loadingTemplates ? 'Loading presets…' : 'Select a preset'}</option>
                    {templates.map((template) => <option key={template.id} value={template.id}>{template.name}</option>)}
                  </select>
                </div>

                {selectedTemplate && (
                  <div className="rounded-md bg-muted/50 p-3 text-sm">
                    <p className="font-medium">{selectedTemplate.name}</p>
                    {selectedTemplate.description && <p className="text-xs text-muted-foreground">{selectedTemplate.description}</p>}
                    <p className="mt-1 text-xs text-muted-foreground">
                      {selectedTemplate.categories.length === 0
                        ? 'Total-points grading (no categories)'
                        : selectedTemplate.categories.map((category) => `${category.name} ${category.weight}%`).join(' · ')}
                    </p>
                  </div>
                )}

                {editing && linkedTemplateId && selectedTemplateId === linkedTemplateId && (
                  <div className={`rounded-md border p-3 text-sm ${editing.gradingTemplateOutOfDate ? 'border-amber-500/50 bg-amber-500/10' : 'bg-muted/30'}`}>
                    {editing.gradingTemplateOutOfDate ? (
                      <p className="flex items-center gap-2"><AlertTriangle className="h-4 w-4" />This course differs from the linked preset. Preview before reapplying.</p>
                    ) : <p>This course matches the linked preset.</p>}
                  </div>
                )}

                {editing && selectedTemplateId && selectedTemplateId !== linkedTemplateId && (
                  <p className="text-xs text-muted-foreground">
                    Selecting a different preset does not change categories. Use Preview apply,
                    review the diff, and then confirm the apply action.
                  </p>
                )}

                {editing && (
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="outline" onClick={handlePreview} disabled={!selectedTemplateId || loading || terminalCommitted}>
                      <RefreshCw className="mr-1 h-4 w-4" />
                      {previewing ? 'Loading diff…' : linkedTemplateId === selectedTemplateId ? 'Preview reapply' : 'Preview apply'}
                    </Button>
                    {linkedTemplateId && (
                      <Button type="button" variant="outline" onClick={handleUnlink} disabled={loading || terminalCommitted}>
                        <Link2Off className="mr-1 h-4 w-4" />Unlink preset
                      </Button>
                    )}
                  </div>
                )}

                {preview && <TemplateDiff preview={preview} />}
                {preview && (
                  <Button
                    type="button"
                    onClick={handleApply}
                    disabled={
                      !preview.applicable ||
                      preview.templateId !== selectedTemplateId ||
                      loading ||
                      terminalCommitted
                    }
                  >
                    {applying ? 'Applying…' : preview.changed ? 'Apply these changes' : 'Reapply unchanged preset'}
                  </Button>
                )}
                {!editing && <p className="text-xs text-muted-foreground">The preset is copied when this gradebook is created. Later preset edits require an explicit reapply.</p>}
              </div>
            ) : (
              <CustomCategoryEditor
                categories={categories}
                weightTotal={weightTotal}
                updateCategory={updateCategory}
                setCategories={setCategories}
              />
            )}
          </div>

          {mode === 'custom' && (
            <div className="space-y-3 rounded-md border p-4">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Checkbox checked={saveAsTemplate} aria-label="Save custom setup as a new template" onCheckedChange={(checked) => setSaveAsTemplate(checked === true)} />
                Save custom setup as a new template
              </label>
              {saveAsTemplate && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="new-template-name">Template name</Label>
                    <Input id="new-template-name" value={newTemplateName} onChange={(event) => setNewTemplateName(event.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-template-description">Description</Label>
                    <Input id="new-template-description" value={newTemplateDescription} onChange={(event) => setNewTemplateDescription(event.target.value)} />
                  </div>
                </div>
              )}
            </div>
          )}

          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          {refreshWarning && (
            <p role="alert" className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm">
              {refreshWarning}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading
              ? 'Saving…'
              : terminalCommitted
                ? 'Close'
                : pendingPresetSeriesId
                  ? 'Retry saving preset'
                  : editing && mode === 'template'
                    ? 'Save details'
                    : editing
                      ? 'Save gradebook'
                      : 'Create gradebook'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CustomCategoryEditor({
  categories,
  weightTotal,
  updateCategory,
  setCategories,
}: {
  categories: CategoryRow[];
  weightTotal: number;
  updateCategory: (index: number, patch: Partial<CategoryRow>) => void;
  setCategories: React.Dispatch<React.SetStateAction<CategoryRow[]>>;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Categories</Label>
        <span className="text-xs text-muted-foreground">Total: {weightTotal.toFixed(2)}%</span>
      </div>
      <div className="grid grid-cols-[2fr_1fr_1fr_auto] gap-2 px-1 text-xs text-muted-foreground">
        <span>Name</span><span>Weight %</span><span>Drop N lowest</span><span />
      </div>
      {categories.map((row, index) => (
        <div key={row.id ?? `new-${index}`} className="grid grid-cols-[2fr_1fr_1fr_auto] items-center gap-2">
          <div className="space-y-1">
            <Input aria-label={`Category name ${index + 1}`} value={row.name} disabled={row.locked} onChange={(event) => updateCategory(index, { name: event.target.value })} placeholder="Essays" />
            {row.locked && (
              <Badge variant="secondary" className="gap-1">
                <Lock className="h-3 w-3" />
                Locked
                <span className="sr-only">
                  {' '}by {(row.lockedByGradebookNames ?? []).join(', ')}
                </span>
              </Badge>
            )}
          </div>
          <Input aria-label={`Category weight ${index + 1}`} type="number" value={row.weight} disabled={row.locked} onChange={(event) => updateCategory(index, { weight: event.target.value })} placeholder="60" />
          <Input aria-label={`Category drop lowest ${index + 1}`} type="number" min="0" value={row.dropLowest} disabled={row.locked} onChange={(event) => updateCategory(index, { dropLowest: event.target.value })} />
          <Button variant="ghost" size="icon" aria-label={`Remove category ${index + 1}`} disabled={row.locked} onClick={() => setCategories((current) => current.filter((_, rowIndex) => rowIndex !== index))}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" disabled={categories.some((row) => row.locked)} onClick={() => setCategories((current) => [...current, emptyCategory()])}>
        <Plus className="mr-1 h-4 w-4" />Add category
      </Button>
      {categories.some((row) => row.locked) && (
        <p className="text-xs text-muted-foreground">
          Reopen every listed gradebook before editing these shared categories.
        </p>
      )}
      <p className="text-xs text-muted-foreground">Leave categories empty to grade on total points instead.</p>
    </div>
  );
}

function TemplateDiff({ preview }: { preview: TemplatePreview }) {
  const sections: Array<[string, ReadonlyArray<CategoryDiff>, string]> = [
    ['Add', preview.additions, 'text-emerald-700'],
    ['Update', preview.updates, 'text-amber-700'],
    ['Remove', preview.removals, 'text-destructive'],
    ['Retain', preview.retained, 'text-blue-700'],
  ];

  return (
    <div className="space-y-2 rounded-md border p-3 text-sm" aria-label="Template change preview">
      <p className="font-medium">Reapply preview</p>
      {!preview.changed && <p className="text-muted-foreground">No changes. This course already matches the preset.</p>}
      {sections.map(([label, rows, className]) => rows.length > 0 && (
        <div key={label}>
          <p className={`text-xs font-semibold uppercase ${className}`}>{label}</p>
          <ul className="list-disc pl-5">
            {rows.map((row) => (
              <li key={`${label}-${row.name}`}>
                {row.name}
                {categoryChangeDetails(label, row).length > 0 &&
                  ` — ${categoryChangeDetails(label, row).join('; ')}`}
                {row.reason && ` — ${row.reason}`}
              </li>
            ))}
          </ul>
        </div>
      ))}
      {preview.retained.length > 0 && (
        <p className="text-xs text-muted-foreground">Retained categories stay course-owned so existing assignment targets are never orphaned.</p>
      )}
      {preview.conflicts.length > 0 && (
        <div role="alert" className="rounded-md bg-destructive/10 p-2 text-destructive">
          {preview.conflicts.join(' ')}
        </div>
      )}
    </div>
  );
}

function categoryChangeDetails(label: string, row: CategoryDiff) {
  if (label === 'Update' && row.current && row.proposed) {
    const changes: string[] = [];
    if (row.current.name !== row.proposed.name) {
      changes.push(`name “${row.current.name}” → “${row.proposed.name}”`);
    }
    if (row.current.weight !== row.proposed.weight) {
      changes.push(`weight ${row.current.weight}% → ${row.proposed.weight}%`);
    }
    if (row.current.dropLowest !== row.proposed.dropLowest) {
      changes.push(`drop lowest ${row.current.dropLowest} → ${row.proposed.dropLowest}`);
    }
    if (row.current.position !== row.proposed.position) {
      changes.push(`order ${row.current.position + 1} → ${row.proposed.position + 1}`);
    }
    return changes;
  }

  const snapshot = label === 'Add' ? row.proposed : row.current;
  if (!snapshot) return [];
  return [
    `weight ${snapshot.weight}%`,
    `drop lowest ${snapshot.dropLowest}`,
    `order ${snapshot.position + 1}`,
  ];
}
